import { ICobro } from '../../services/api-services/CobrosServices'
import { ITarifa } from './../../services/api-services/TarifasServices'

export type VariablesEntrada = {
  [key: string]: number
  unidad: number // obligatorio
}

export interface SemanticCheckResult {
  ok: boolean
  variablesExtra: string[]
  referencias: number[]
  funciones: string[]
  errores: string[]
}

// ======================================================
// ENUM DE FUNCIONES PERMITIDAS
// ======================================================
export enum FuncionesPermitidas {
  FLOOR = 'floor',
  CEIL = 'ceil',
  ROUND = 'round',
  ABS = 'abs',
  MIN = 'min',
  MAX = 'max',
}

export function getNombreFuncionImprimible(func: FuncionesPermitidas): string {
  switch (func) {
    case FuncionesPermitidas.FLOOR:
      //return 'Redondear hacia abajo'
      return ''
    case FuncionesPermitidas.CEIL:
      //return 'Redondear hacia arriba'
      return ''
    case FuncionesPermitidas.ROUND:
      //return 'Redondear al entero más cercano'
      return ''
    case FuncionesPermitidas.ABS:
      return 'Valor absoluto'
    case FuncionesPermitidas.MIN:
      return 'Mínimo'
    case FuncionesPermitidas.MAX:
      return 'Máximo'
    default:
      return 'Desconocida'
  }
}

export default class MotorMatematico {
  // Reglas del lenguaje
  public static variableRegex = /\[([a-zA-Z_][a-zA-Z0-9_]*)\]/g
  public static referenciaRegex = /\{(\d+)\}/g

  // detecta solo funciones permitidas
  public funcionesRegex: RegExp

  // detecta cualquier nombre_de_funcion(
  public funcionGenericaRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g

  constructor(private tarifas: ITarifa[]) {
    const funcs = Object.values(FuncionesPermitidas).join('|')
    this.funcionesRegex = new RegExp(`\\b(${funcs})\\b`, 'g')
  }

  // ======================================================
  //  VERIFICACION SEMANTICA COMPLETA
  // ======================================================
  public verificarSemantica(tarifa: ITarifa): SemanticCheckResult {
    const formula = tarifa.calculo
    const errores: string[] = []
    const variablesExtra = new Set<string>()
    const referencias = new Set<number>()
    const funciones = new Set<string>()

    // 1 Buscar variables [var]
    let varMatch
    while ((varMatch = MotorMatematico.variableRegex.exec(formula))) {
      const variable = varMatch[1]
      if (variable !== 'valor' && variable !== 'unidad') {
        variablesExtra.add(variable)
      }
    }

    // 2 Buscar referencias {codigo}
    let refMatch
    while ((refMatch = MotorMatematico.referenciaRegex.exec(formula))) {
      const codigo = parseInt(refMatch[1])
      referencias.add(codigo)

      const tRef = this.tarifas.find((t) => t.codigo_sap === codigo)
      if (!tRef) {
        errores.push(`La tarifa referenciada {${codigo}} no existe`)
        continue
      }
      if (tRef.unidad_medida !== tarifa.unidad_medida) {
        errores.push(
          `Unidad incompatible: ${tarifa.codigo_sap} (${tarifa.unidad_medida}) → {${codigo}} (${tRef.unidad_medida})`,
        )
      }
    }

    // 3 Validacion avanzada de funciones matematicas
    const resultadoFunciones = this.verificarFuncionesMatematicas(formula)
    resultadoFunciones.funciones.forEach((f) => funciones.add(f))
    resultadoFunciones.errores.forEach((e) => errores.push(e))

    // 4 Verificacion de caracteres ilegales
    if (/[^0-9+\-*/(){}\[\]\s.,a-zA-Z_]/.test(formula)) {
      errores.push('La fórmula contiene caracteres no permitidos')
    }

    // 5 Verificar corchetes balanceados
    let balance = 0
    for (const c of formula) {
      if (c === '[') balance++
      else if (c === ']') balance--
      if (balance < 0) {
        errores.push('Corchete cerrado sin abrir')
        break
      }
    }
    if (balance > 0) errores.push('Hay corchetes sin cerrar')

    // 6 Detectar falta de operadores entre valores o parentesis
    const formulaSinRefs = formula.replace(/\{\d+\}/g, '0')
    const faltaOperadorRegex = /(\]|\d+|\))\s*(?=\[|\(|\d)/g
    let m
    while ((m = faltaOperadorRegex.exec(formulaSinRefs))) {
      errores.push(`Falta operador entre valores o paréntesis cerca de "${m[0]}"`)
    }

    return {
      ok: errores.length === 0,
      variablesExtra: Array.from(variablesExtra),
      referencias: Array.from(referencias),
      funciones: Array.from(funciones),
      errores,
    }
  }

  // ======================================================
  // VALIDACIÓN SIMPLE
  // ======================================================
  public validarFormula(tarifa: ITarifa, variablesExtra: string[] = []): string {
    const formula = tarifa.calculo

    // 1 Validar variables
    let varMatch
    while ((varMatch = MotorMatematico.variableRegex.exec(formula))) {
      const variable = varMatch[1]

      if (variable !== 'valor' && variable !== 'unidad' && !variablesExtra.includes(variable)) {
        return `Variable desconocida: [${variable}] en tarifa ${tarifa.codigo_sap}`
      }
    }

    // 2 Validar referencias
    let refMatch
    while ((refMatch = MotorMatematico.referenciaRegex.exec(formula))) {
      const codigoRef = parseInt(refMatch[1])
      const tarifaRef = this.tarifas.find((t) => t.codigo_sap === codigoRef)

      if (!tarifaRef) {
        return `La tarifa referenciada {${codigoRef}} no existe`
      }
      if (tarifaRef.unidad_medida !== tarifa.unidad_medida) {
        return `La tarifa ${tarifa.codigo_sap} referencia {${codigoRef}} con unidad distinta (${tarifaRef.unidad_medida})`
      }
    }

    // 3 Validar funciones inexistentes
    const permitidas = Object.values(FuncionesPermitidas)
    let funcMatch
    while ((funcMatch = this.funcionGenericaRegex.exec(formula))) {
      const nombreFuncion = funcMatch[1]

      if (!permitidas.includes(nombreFuncion as FuncionesPermitidas)) {
        return `Función matemática desconocida: ${nombreFuncion}()`
      }
    }

    return 'OK'
  }

  // ======================================================
  // INTERPRETACIÓN
  // ======================================================
  public interpretar(codigoTarifa: number, variables: VariablesEntrada): number {
    const tarifa = this.tarifas.find((t) => t.codigo_sap === codigoTarifa)
    if (!tarifa) throw new Error(`Tarifa ${codigoTarifa} no encontrada`)

    if ('valor' in variables) {
      tarifa.valor = variables['valor']
    }

    const cache: Record<number, number> = {}
    const pila = new Set<number>()

    return this._interpretarTarifa(tarifa, variables, cache, pila)
  }

  private _interpretarTarifa(
    tarifa: ITarifa,
    variables: VariablesEntrada,
    cache: Record<number, number>,
    pila: Set<number>,
  ): number {
    if (cache[tarifa.codigo_sap] !== undefined) {
      return cache[tarifa.codigo_sap]
    }

    if (pila.has(tarifa.codigo_sap)) {
      throw new Error(`Ciclo detectado al evaluar tarifa ${tarifa.codigo_sap}`)
    }
    pila.add(tarifa.codigo_sap)

    let expr = tarifa.calculo

    // 1️⃣ Reemplazar variables
    expr = expr.replace(MotorMatematico.variableRegex, (_, varName: string) => {
      if (varName === 'valor') return String(tarifa.valor)
      if (varName === 'unidad') return String(variables.unidad)
      if (variables[varName] === undefined) throw new Error(`Variable [${varName}] no proporcionada`)
      return String(variables[varName])
    })

    // 2️⃣ Reemplazar referencias
    expr = expr.replace(MotorMatematico.referenciaRegex, (_, codigoRefStr: string) => {
      const codigoRef = parseInt(codigoRefStr)
      const tarifaRef = this.tarifas.find((t) => t.codigo_sap === codigoRef)

      if (!tarifaRef) {
        throw new Error(`Tarifa {${codigoRef}} no existe`)
      }
      if (tarifaRef.unidad_medida !== tarifa.unidad_medida) {
        throw new Error(`Unidades incompatibles en referencia {${codigoRef}}`)
      }

      return String(this._interpretarTarifa(tarifaRef, variables, cache, pila))
    })

    // 3️⃣ Funciones matemáticas permitidas en el scope
    const scope: Record<string, any> = {
      floor: Math.floor,
      ceil: Math.ceil,
      round: Math.round,
      abs: Math.abs,
      min: Math.min,
      max: Math.max,
    }

    // 4️⃣ Evaluación final controlada
    let resultado: number
    try {
      const f = new Function(...Object.keys(scope), `return (${expr});`)

      resultado = f(...Object.values(scope))
    } catch (e: any) {
      throw new Error(`Error al interpretar fórmula de tarifa ${tarifa.codigo_sap}: ${e.message}`)
    }

    cache[tarifa.codigo_sap] = resultado
    pila.delete(tarifa.codigo_sap)

    return resultado
  }

  public obtenerTarifasReferenciadoras(codigo: number): ITarifa[] {
    const resultado: ITarifa[] = []
    const regex = /\{(\d+)\}/g

    for (const tarifa of this.tarifas) {
      let match
      while ((match = regex.exec(tarifa.calculo))) {
        const codigoRef = parseInt(match[1])
        if (codigoRef === codigo) {
          resultado.push(tarifa)
          break // no revisar más referencias dentro de esta misma tarifa
        }
      }
    }

    return resultado
  }

  private verificarFuncionesMatematicas(formula: string) {
    const errores: string[] = []
    const funcionesValidas = new Set<string>(Object.values(FuncionesPermitidas))
    const funcionesEncontradas = new Set<string>()

    // 1️⃣ Detectar cualquier palabra que parezca función: nombre(
    const regex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*(\([^)]*\))?/g
    let match

    while ((match = regex.exec(formula))) {
      const nombreFuncion = match[1]
      const parentesis = match[2] // undefined si no tiene paréntesis

      // 2️⃣ Verificar si la función existe
      if (!funcionesValidas.has(nombreFuncion)) {
        continue // ignoramos nombres que no son funciones permitidas
      }

      funcionesEncontradas.add(nombreFuncion)

      // 3️⃣ Verificar que tenga paréntesis
      if (!parentesis) {
        errores.push(`Función matemática ${nombreFuncion}() llamada sin paréntesis`)
        continue
      }

      // 4️⃣ Extraer argumentos dentro del paréntesis
      const argsTexto = parentesis.slice(1, -1).trim() // remover paréntesis

      const args = argsTexto.length > 0 ? separarArgs(argsTexto) : []

      // 5️⃣ Validar número de argumentos según función
      const validarArgs = (min: number, max?: number) => {
        if (args.length < min) {
          errores.push(`La función ${nombreFuncion}() requiere al menos ${min} argumento(s)`)
        }
        if (max !== undefined && args.length > max) {
          errores.push(`La función ${nombreFuncion}() admite máximo ${max} argumento(s)`)
        }
      }

      switch (nombreFuncion) {
        case 'floor':
        case 'ceil':
        case 'round':
        case 'abs':
          validarArgs(1, 1) // exactamente 1 argumento
          break
        case 'min':
        case 'max':
          validarArgs(2) // mínimo 2 argumentos
          break
      }

      // 6️⃣ Verificar que ningún argumento esté vacío
      if (args.some((a) => a === '')) {
        errores.push(`La función ${nombreFuncion}() tiene argumentos vacíos`)
      }
    }

    return {
      errores,
      funciones: Array.from(funcionesEncontradas),
    }
  }

  public static reemplazarVariables(tarifa: ITarifa): string {
    if (!tarifa) throw new Error('Tarifa no proporcionada')

    let formula = tarifa.calculo

    // 1️⃣ Reemplazar variables [valor] y [unidad]
    formula = formula.replace(this.variableRegex, (_, varName: string) => {
      if (varName === 'valor') return String(tarifa.valor)
      if (varName === 'unidad') return tarifa.unidad_medida // nombre de la unidad
      return `[${varName}]` // deja intactas las variables extra
    })

    // 2️⃣ Reemplazar referencias {codigo} por tarifa(codigo)
    formula = formula.replace(this.referenciaRegex, (_, codigoStr: string) => {
      return `tarifa(${codigoStr})`
    })

    return formula
  }

  public static reemplazarVariablesFull(tarifa: ITarifa, entrada: VariablesEntrada): string {
    if (!tarifa) throw new Error('Tarifa no proporcionada')

    let formula = tarifa.calculo

    // 1️⃣ Reemplazar variables [valor] y [unidad]
    formula = formula.replace(this.variableRegex, (_, varName: string) => {
      if (varName === 'valor') return String(tarifa.valor)
      if (varName === 'unidad') return tarifa.unidad_medida // nombre de la unidad
      if (varName in entrada) return String(entrada[varName])
      return `[${varName}]` // deja intactas las variables extra inexistentes
    })

    // 2️⃣ Reemplazar referencias {codigo} por tarifa(codigo)
    formula = formula.replace(this.referenciaRegex, (_, codigoStr: string) => {
      return `tarifa(${codigoStr})`
    })

    return formula
  }

  public reemplazarVariablesFromCobro(cobro: ICobro): string {
    const tarifa = this.tarifas.find((t) => t.codigo_sap === cobro.codigo_sap)
    if (!tarifa) throw new Error(`Tarifa ${cobro.codigo_sap} no encontrada`)

    let formula = tarifa.calculo

    // 1️⃣ Reemplazar variables [valor] y [unidad]
    formula = formula.replace(MotorMatematico.variableRegex, (_, varName: string) => {
      if (varName === 'valor') return 'VALOR_APLICADO'
      if (varName === 'unidad') return tarifa.unidad_medida // nombre de la unidad
      return `${varName}` // deja intactas las variables extra inexistentes
    })

    // 2️⃣ Reemplazar nombres de funciones matematicas por nombre imprimible
    formula = formula.replace(this.funcionesRegex, (match, funcName: string) => {
      return getNombreFuncionImprimible(funcName as FuncionesPermitidas)
    })

    // 3️⃣ Reemplazar referencias {codigo} por tarifa(codigo)
    formula = formula.replace(MotorMatematico.referenciaRegex, (_, codigoStr: string) => {
      return `tarifa(${codigoStr})`
    })

    return formula
  }

  public reemplazarVariablesFromCobroFull(cobro: ICobro, entrada: VariablesEntrada): string {
    const tarifa = this.tarifas.find((t) => t.codigo_sap === cobro.codigo_sap)
    if (!tarifa) throw new Error(`Tarifa ${cobro.codigo_sap} no encontrada`)

    let formula = tarifa.calculo

    // 1️⃣ Reemplazar variables [valor] y [unidad]
    formula = formula.replace(MotorMatematico.variableRegex, (_, varName: string) => {
      if (varName === 'valor') return toMoney(cobro.valor_aplicado)
      if (varName === 'unidad') return cobro.unidades_aplicadas.toString() // nombre de la unidad
      if (varName in entrada) return String(entrada[varName])
      return `[${varName}]` // deja intactas las variables extra inexistentes
    })

    // 2️⃣ Reemplazar nombres de funciones matematicas por nombre imprimible
    formula = formula.replace(this.funcionesRegex, (match, funcName: string) => {
      return getNombreFuncionImprimible(funcName as FuncionesPermitidas)
    })

    // 3️⃣ Reemplazar referencias {codigo} por tarifa(codigo)
    formula = formula.replace(MotorMatematico.referenciaRegex, (_, codigoStr: string) => {
      return `tarifa(${codigoStr})`
    })

    return formula
  }
}

function separarArgs(argStr: string): string[] {
  const args: string[] = []
  let nivel = 0
  let current = ''

  for (let i = 0; i < argStr.length; i++) {
    const c = argStr[i]
    if (c === '(') nivel++
    if (c === ')') nivel--
    if (c === ',' && nivel === 0) {
      args.push(current.trim())
      current = ''
    } else {
      current += c
    }
  }

  if (current.trim() !== '') args.push(current.trim())
  return args
}

export function toMoney(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })
}
